import {IAllWinData, IWinRatioConfig} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {Presentation} from "../../../../../core/gameflow/controller/Presentation";
import {StateHandler} from "../../../../../core/utils/StateHandler";
import {WinGameData} from "../../../../server/data/GameDataModels";
import {GameConfig} from "../../../../config/base/GameConfig";
import {SoundConfig} from "../../../../sound/SoundConfig";
import {SoundManager} from "../../../../sound/SoundManager";
import {PunkSymbol} from "../../../../punkEngine/symbol/PunkSymbol";
import {PREngineManager} from "../../../../punkEngine/PREngineManager";
import {PunkRocker} from "../../../../PunkRocker";
import {PosterManager} from '../../../independent/Poster/PosterManager';
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {GameData} from "../../../../server/data/GameData";
import {PRGameMode} from "../../../../server/PRGameMode";
import {NJReqIndividualWinView} from "../normal/NJReqIndividualWinView";

class BigWinStates extends StateHandler {
    public static readonly START: string = "start";
    public static readonly SHOW: string = "show";
    public static readonly STOP: string = "stop";
    public static readonly DONE: string = "done";
}

export class BigWinPresentation extends Presentation {

    private _winRatios: IWinRatioConfig[];
    private _winData: WinGameData;

    private _winLevel: number = 0;
    private _currLevel: number = 0;

    private _bounceDuration: number = 0.25;
    private _bigWinStates: BigWinStates = new BigWinStates();

    private isTotalWin: boolean = false;

    private fsLowBigWin: boolean = false;
    private isBelowStake: boolean = false;

    private _countUpValue: number = 0;

    private _isBonusGame: boolean = false;
    private _brokeTheBank: boolean = false;
    private _nextMode: string;

    initialization(): void {
        this._winRatios = GameConfig.WIN_RATIOS.bigWin;
    }

    public setGamedData(data: GameData): void {
        this._winData = data.Win;
        this._nextMode = data.nextMode;
        this._isBonusGame = data.isFreeSpin;
        this._brokeTheBank = data.raw.brokeTheBank;

        this.isBelowStake = data.Win.isWinBelowStake!();

        this.isTotalWin = data.mode !== GameMode.NORMAL || data.nextMode !== GameMode.NORMAL;
        this._winLevel = this._winRatios.findIndex((indx) => indx.ratio == this._winData.winRatioConfig?.ratio);

        this.fsLowBigWin = ((data.mode === PRGameMode.FREESPIN_LEVEL_ONE) || (data.mode === PRGameMode.FREESPIN_LEVEL_TWO) || (data.mode === PRGameMode.FREESPIN_LEVEL_THREE)) && this._winLevel < 2;
        this._countUpValue = this._winData!.totalWin - this._winData!.singleWin;
    }

    preStart(): void {
        this._currLevel = 0;
        this._bigWinStates.set(BigWinStates.START);
    }

    public checkForActivation(data: GameData): boolean {
        return data.Win.isBigWin;
    }

    public createPresentation(enableSkip: Function): void {
        enableSkip(this._bigWinStates.equal(BigWinStates.SHOW));

        switch (this._bigWinStates.value) {
            case BigWinStates.START:
                this.preWin();
                break;
            case BigWinStates.SHOW:
                this.playBigWinLevelAnimation();
                break;
            case BigWinStates.STOP:
                this.postWin();
                break;
            case BigWinStates.DONE:
                this.PresentationState.setInactive();
                break;
            default:
                break;
        }
    }

    public playBigWinLevelAnimation(): void {
        const presLvl: number = this._currLevel;

        if (presLvl >= this._winLevel) { // Last lvl Before PostWin
            // #285
            const countUpDurr: number = this._winRatios[this._winLevel].duration;
            if (presLvl > 0) {
                this.timeline.add(this.playBigWinCountUp(this._winLevel, countUpDurr), 0);
            } else {
                this.timeline.to(this, {
                    duration: countUpDurr
                });
            }

            if (presLvl > 0 && presLvl < 5) {
                this.playAnimLoop(presLvl);
            }
            this._bigWinStates.set(BigWinStates.STOP);

        } else {
            // #285
            const countUpDurr: number = GameConfig.WIN_RATIOS.bigWin[presLvl].duration;
            if (presLvl > 0) {
                this.timeline.add(this.playBigWinCountUp(presLvl, countUpDurr), 0);
            } else {
                this.timeline.to(this, {
                    duration: countUpDurr
                });
            }
            if (presLvl > 0 && this._winLevel !== presLvl && presLvl < 5) {
                this.playAnimLoop(presLvl);
            }
        }

        if (this.overFlowSkip(presLvl, 4)) {
            this._bigWinStates.set(BigWinStates.STOP);
        } else
            this._currLevel++;
    }

    public done(): void {
        PREngineManager.RSAll((rc, sym) => sym.markNonWin(false));
    }

    private preWin(): void {
        const applyDuration: number = 0.5,
            reelWinPosData: boolean[][] = this._winData.allBetWayWinData.reelWinPositions;

        const allWins: IAllWinData = this._winData.allBetWayWinData!;

        if (allWins.symbols.length === 1 && !(this.isBelowStake)) {
            for (let reel: number = 0; reel < allWins.reelWinPositions.length; reel++) {
                for (let row: number = 0; row < allWins.reelWinPositions[reel].length; row++) {
                    const sym: PunkSymbol | undefined = PREngineManager.getReel(reel).getSymbol(row),
                        lastWinSymId: number = allWins.reelWinPositions[reel].lastIndexOf(true),
                        lastWinSym: PunkSymbol | undefined = PREngineManager.getReel(reel).getSymbol(lastWinSymId);

                    if (sym) {
                        if (reel === (allWins.longest - 1) && (row === lastWinSymId) && lastWinSym && allWins.symbols.length === 1) {
                            const posXBuffer: number = allWins.reelWinPositions[reel].length === 3 ? 0 : -5,
                                posYBuffer: number = allWins.reelWinPositions[reel].length === 3 ? 0 : 0,
                                winWaysObj: NJReqIndividualWinView = new NJReqIndividualWinView(this._winData.betWayWins[0].multipliedAmount, allWins.totalWinCombination, new PIXI.Point((PREngineManager.getReel(reel).position.x + posXBuffer), (lastWinSym.position.y + posYBuffer)));

                            PunkRocker.fadedWinView.addWinWays(winWaysObj);
                            this.timeline.add(() => {
                                winWaysObj.showWinAndWays(0.75).eventCallback("onComplete", () => {
                                    PunkRocker.fadedWinView.removeWinWays(winWaysObj);
                                });
                            }, 0);
                        }
                    }
                }
            }
        }

        this.timeline.add(() => {
            SoundManager.playSoundEffect(SoundConfig.BIG_WIN_INITIAL);
            SoundManager.fadeAmbienceLoop(0, 300);
        }, 0);

        for (let reelId: number = 0; reelId < reelWinPosData.length; reelId++) {
            const currReelWinData: boolean[] = reelWinPosData[reelId];
            for (let symId: number = 0; symId < currReelWinData.length; symId++) {
                const currSym: PunkSymbol | undefined = PREngineManager.getReel(reelId).getSymbol(symId),
                    hits: boolean = this._winData.allBetWayWinData.reelWinPositions[reelId][symId];

                if (currSym) {
                    this.timeline.add(() => currSym.playBigWin(hits, applyDuration, this._isBonusGame), 0);
                }
            }
        }

        const requestedDelay: number = 989, //#277
            calcDelay: number = parseFloat(((requestedDelay % 60000) / 1000).toFixed(3));

        this.timeline.add([
            PosterManager.instance.playBigWin(0),
            () => {
                SoundManager.playSoundEffect(SoundConfig.BIG_WIN_MUSIC, 3);

                // #285 => added the time of this count up of level 0 in the "playBigWinLevelAnimation" method. Don't add this time here in the timeline.
                PunkRocker.bigWinView.show(0.2);
                const countUpDurr: number = GameConfig.WIN_RATIOS.bigWin[0].duration;
                this.playBigWinCountUp(0, countUpDurr);
            }
        ], calcDelay);

        this._bigWinStates.set(BigWinStates.SHOW);
    }

    private playAnimLoop(lvl: number) {
        PosterManager.instance.playBigWin(lvl)
    }

    private postWin(): void {
        const stopDurr: number = 0.75;
        SoundManager.playSoundEffect(SoundConfig.BIG_WIN_MUSIC_END);
        SoundManager.stopSoundEffect(SoundConfig.BIG_WIN_MUSIC);
        SoundManager.stopBigWin();
        PosterManager.instance.stop(1.5);

        // #335
        this.timeline.add(PunkRocker.bigWinView.onCountUpEnd());

        this.timeline.add([
            () => {
                if (this._brokeTheBank) {
                    SoundManager.fadeAmbienceLoop(0, 1500);
                } else {
                    if (this._isBonusGame) {
                        SoundManager.playAmbientLoop(this._nextMode);
                        SoundManager.fadeAmbienceLoop(1, 1500);
                    } else {
                        SoundManager.crossFadeToMain2(1500, true);
                    }
                }
            },
            PunkRocker.bigWinView.hide(0.4),
        ], this._bounceDuration + 0.15);
        this.timeline.add(() => PunkRocker.keypadPlugin.setWin(this._winData!.totalWin, this.isTotalWin, this.isBelowStake));

        const delay: number = this.timeline.totalDuration();

        for (let reel = 0; reel < this._winData.allBetWayWinData.reelWinPositions.length; reel++) {
            for (let row = 0; row < this._winData.allBetWayWinData.reelWinPositions[reel].length; row++) {
                const sym: PunkSymbol | undefined = PREngineManager.getReel(reel).getSymbol(row);
                if (sym) {
                    if (this._winData.allBetWayWinData.reelWinPositions[reel][row]) {
                        // this.timeline.add(() => sym.bigWin(false), delay + 0.1);
                    }
                    this.timeline.add(() => sym.stopWin(stopDurr), delay + reel * 0.15);
                }
            }
        }

        this.timeline.to(this, {duration: stopDurr});

        const from: number = this._winData!.totalWin - this._winData!.singleWin;
        this.timeline.fromTo(this, {countUpValue: from}, {countUpValue: this._winData!.totalWin, duration: 0.35}, 0)

        if (this._brokeTheBank) {
            this.timeline.progress(1);
        }
        this._bigWinStates.set(BigWinStates.DONE);
    }

    private overFlowSkip(level: number, xRatio: number = 3): boolean {
        let restXVal: number = (this.nextValueRatio(level + 1) - this.betValueRatio(level + 1)) / this._winData!.playedBetValue;
        return restXVal >= 0 && restXVal < (xRatio + (level * xRatio));
    }

    private playBigWinCountUp(level: number, duration: number): gsap.core.Timeline {
        const fromValue: number = level === 0 ? 1 : this.betValueRatio(level);
        let toValue: number = this.nextValueRatio(level);

        if (this.overFlowSkip(level, 3)) toValue = this.nextValueRatio(level + 1)

        return PunkRocker.bigWinView.getLevelAnimation(fromValue, toValue, duration, level);
    }

    private betValueRatio(level: number): number {
        return this._winData!.playedBetValue * GameConfig.WIN_RATIOS.bigWin[Math.min(level, GameConfig.WIN_RATIOS.bigWin.length - 1)].ratio;
    }

    private nextValueRatio(level: number): number {
        return level + 1 > this._winLevel ? this._winData!.singleWin : this.betValueRatio(level + 1);
    }

}